import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import { ProductRelease } from '../Product.interface';
import { useFindProductReleases } from '../Product.hooks';

// compare IDs of both arrays
function areIDsEqual<Entity>(
  arr1: Entity[],
  arr2: Entity[],
  mapEntityId: (entity: Entity) => number = (entity: Entity) => {
    return (entity as any).id;
  }
): boolean {
  return isEqual(arr1.map(mapEntityId).sort(), arr2.map(mapEntityId).sort());
}

export interface ProductReleasesSelectorProps {
  productId: number;
  disabled?: boolean;
  value?: ProductRelease[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onSelected?: (releases: ProductRelease[]) => void;
  onChange?: (releases: ProductRelease[]) => void;
}

const ProductReleasesSelector: React.FC<ProductReleasesSelectorProps> = (
  props: ProductReleasesSelectorProps
) => {
  const {
    productId,
    value: initialReleases,
    disabled,
    textFieldProps,
    onChange,
    onSelected
  } = props;
  const previousReleases = useRef<ProductRelease[]>(initialReleases || []);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<ProductRelease[]>([]);
  const [releases, setReleases] = useState<ProductRelease[]>([]);
  const debouncedSearch: string = useDebounce(search, 300);
  const {
    productReleases: data,
    isLoading,
    loadingError: error,
    findProductReleases: get
  } = useFindProductReleases(productId);

  const params: ApiParams = useMemo(() => {
    const params: ApiParams = {};
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    return params;
  }, [debouncedSearch]);

  useEffect(() => {
    get(params);
  }, [get, params]);

  useEffect(() => {
    data && setReleases(data);
  }, [data]);

  // if initialReleaseIds changed, reselect releases
  useEffect(() => {
    if (!areIDsEqual(previousReleases.current, initialReleases || [])) {
      setSelected([]);
      previousReleases.current = initialReleases || [];
      // refetch
      get(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReleases]);

  useEffect(() => {
    // if no selected release yet
    if (selected.length > 0) {
      return;
    }
    const selectedReleases: ProductRelease[] = releases.filter(
      (release: ProductRelease) => {
        return initialReleases?.some(
          (c: ProductRelease) => c.id === release.id
        );
      }
    );
    if (selectedReleases.length > 0) {
      setSelected(selectedReleases);
      onSelected?.(selectedReleases);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releases, initialReleases]);

  return (
    <Autocomplete
      multiple
      loading={isLoading}
      disabled={disabled}
      value={selected}
      options={releases}
      getOptionLabel={(option: ProductRelease) => option.name}
      getOptionSelected={(option: ProductRelease) =>
        !!initialReleases?.some(
          (release: ProductRelease) => release.id === option.id
        )
      }
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_event: React.ChangeEvent<{}>, value: ProductRelease[]) => {
        previousReleases.current = value;
        setSelected(value);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Product Releases'
          placeholder='Choose Product Releases'
          helperText={error?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default ProductReleasesSelector;
